import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, ArrowRight, Linkedin, Facebook, Twitter } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const services = [
    'Oil & Gas Services',
    'Mechanical & Engineering',
    'Procurement Services',
    'Drilling Services',
    'Civil & Construction',
    'Marine & Logistics',
    'Equipment Rentals',
  ];

  const quickLinks = [
    { name: 'About Us', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Projects', path: '/projects' },
    { name: 'HSE & CSR', path: '/hse-csr' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <footer className="bg-[#0A0A0A] text-white border-t-4 border-[#FF6B00]" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <div className="mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_godston-preview/artifacts/7ulqxgtg_WhatsApp%20Image%202026-03-18%20at%2017.43.02.jpeg" 
                alt="Godston Projects Limited Logo"
                className="h-24 w-auto object-contain bg-white p-2 rounded"
              />
            </div>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              Indigenous engineering firm providing marine, logistics, procurement, oilfield, construction, and project management services across Sub-Saharan Africa and the UK.
            </p>
            <div className="flex gap-4">
              <a 
                href="#" 
                className="w-10 h-10 bg-slate-800 hover:bg-[#FF6B00] flex items-center justify-center transition-colors"
                data-testid="social-linkedin"
              >
                <Linkedin size={18} />
              </a>
              <a 
                href="#" 
                className="w-10 h-10 bg-slate-800 hover:bg-[#FF6B00] flex items-center justify-center transition-colors"
                data-testid="social-facebook"
              >
                <Facebook size={18} />
              </a>
              <a 
                href="#" 
                className="w-10 h-10 bg-slate-800 hover:bg-[#FF6B00] flex items-center justify-center transition-colors"
                data-testid="social-twitter"
              >
                <Twitter size={18} />
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-bold text-lg font-['Barlow_Condensed'] uppercase tracking-wider mb-6">
              Our Services
            </h4>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service}>
                  <Link 
                    to="/services" 
                    className="text-slate-400 hover:text-[#FF6B00] text-sm transition-colors flex items-center gap-2 group"
                  >
                    <ArrowRight size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                    <span>{service}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-bold text-lg font-['Barlow_Condensed'] uppercase tracking-wider mb-6">
              Quick Links
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path} 
                    className="text-slate-400 hover:text-[#FF6B00] text-sm transition-colors flex items-center gap-2 group"
                    data-testid={`footer-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <ArrowRight size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                    <span>{link.name}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-bold text-lg font-['Barlow_Condensed'] uppercase tracking-wider mb-6">
              Contact Us
            </h4>
            <div className="space-y-4">
              <div>
                <p className="text-[#FF6B00] text-xs uppercase tracking-wider mb-1">UK Office</p>
                <div className="flex items-start gap-3 text-slate-400 text-sm">
                  <MapPin size={16} className="mt-1 flex-shrink-0" />
                  <span>44 Linton Gardens, London E6 5SQ</span>
                </div>
              </div>
              <div>
                <p className="text-[#FF6B00] text-xs uppercase tracking-wider mb-1">Nigeria Office</p>
                <div className="flex items-start gap-3 text-slate-400 text-sm">
                  <MapPin size={16} className="mt-1 flex-shrink-0" />
                  <span>Warri, Delta State, Nigeria</span>
                </div>
              </div>
              <div className="flex items-center gap-3 text-slate-400 text-sm">
                <Phone size={16} className="flex-shrink-0" />
                <div>
                  <a href="tel:+447915697011" className="hover:text-[#FF6B00] transition-colors block">
                    +44 7915 697011 (UK)
                  </a>
                  <a href="tel:+2348107349363" className="hover:text-[#FF6B00] transition-colors block">
                    +234 810 734 9363 (NG)
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-3 text-slate-400 text-sm">
                <Mail size={16} className="flex-shrink-0" />
                <div>
                  <a href="mailto:admin@godstonprojects.org" className="hover:text-[#FF6B00] transition-colors block">
                    admin@godstonprojects.org
                  </a>
                  <a href="mailto:epetegp@godstonprojects.org" className="hover:text-[#FF6B00] transition-colors block">
                    epetegp@godstonprojects.org
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="py-6 border-t border-slate-800 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-500 text-sm">
            &copy; {currentYear} Godston Projects Limited. All rights reserved.
          </p>
          <div className="flex gap-6 text-sm">
            <a href="#" className="text-slate-500 hover:text-[#FF6B00] transition-colors">
              Privacy Policy
            </a>
            <a href="#" className="text-slate-500 hover:text-[#FF6B00] transition-colors">
              Terms of Service
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
